// @see https://developer.zendesk.com/documentation/classic-web-widget-sdks/web-widget/getting-started/customizing-web-widget-events-tracking-to-third-party-analytics-services/
// Custom launcher (no default FAB): https://support.zendesk.com/hc/en-us/articles/4500747797914
// Core API: https://developer.zendesk.com/api-reference/widget-messaging/web/core/
import { useClient } from '@/composables/useClient'
import { useFeatureFlags } from '@/composables/useFeatureFlags'
import { useAuthStore } from '@/stores/auth'
import { watchEffect } from 'vue'

type ZendeskEmbedApi = (...args: unknown[]) => void

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    zE?: ZendeskEmbedApi
  }
}

const SCRIPT_ID = 'ze-snippet'

/** Used in script onload if the user logs out before Zendesk finishes loading. */
let userWantsZendesk = false

/** One close listener per `window.zE` instance (tests swap mocks; production keeps one embed API). */
const messengerCloseRehideRegisteredForEmbed = new WeakSet<ZendeskEmbedApi>()

function runZendeskCommand(...command: unknown[]) {
  const zendeskEmbed = window.zE

  if (typeof zendeskEmbed === 'function') zendeskEmbed(...command)
}

function getZendeskWidgetKey(): string | undefined {
  const rawKey = import.meta.env.VITE_ZENDESK_WIDGET_KEY

  if (typeof rawKey !== 'string') return undefined

  const trimmedKey = rawKey.trim()

  return trimmedKey.length > 0 ? trimmedKey : undefined
}

/**
 * Keep conversation chrome headers visible (minimize / chevron) on all breakpoints Zendesk exposes here.
 * If the control still differs by viewport, adjust Web Widget Style in Admin Center.
 */
function applyMessengerHeaderAlwaysVisible(): void {
  runZendeskCommand('messenger:set', 'customization', {
    common: { hideHeader: false },
    conversationList: { hideHeader: false },
    messageLog: { hideHeader: false },
  })
}

/** After panel close, Zendesk may re-show the launcher; `hide` keeps API-only entry until next `open`. */
function ensureMessengerCloseRehidesLauncher(): void {
  const zendeskEmbed = window.zE

  if (typeof zendeskEmbed !== 'function' || messengerCloseRehideRegisteredForEmbed.has(zendeskEmbed)) return

  messengerCloseRehideRegisteredForEmbed.add(zendeskEmbed)

  zendeskEmbed('messenger:on', 'close', () => {
    runZendeskCommand('messenger', 'hide')
  })
}

/**
 * Opens Messaging from in-app controls (e.g. Contact us → Live chat now).
 * Pair with **Custom launcher** in Zendesk Admin so no default FAB is injected on the page.
 */
export function openZendeskMessenger(): void {
  if (!getZendeskWidgetKey() || typeof window.zE !== 'function') return

  runZendeskCommand('messenger', 'show')
  runZendeskCommand('messenger', 'open')
}

/** Tyga parity: JWT via BFF `GET /zendesk/messaging-token`. */
async function runZendeskMessengerJwtLogin(): Promise<void> {
  const zendeskEmbed = window.zE

  if (typeof zendeskEmbed !== 'function') return

  zendeskEmbed(
    'messenger',
    'loginUser',
    async (callback: (jwt: string) => void) => {
      try {
        const client = await useClient()
        const response = await client.zendesk.getMessagingToken()

        if (response.status === 200) {
          callback(response.body.token)
        } else {
          console.error('Zendesk messaging token request failed', {
            status: response.status,
            body: 'body' in response ? response.body : undefined,
          })
        }
      } catch (error) {
        console.error('Zendesk messaging token request threw', { error })
      }
    },
    (error: unknown) => {
      if (error) {
        console.error('Failed to authenticate with Zendesk', { error })
      }
    },
  )
}

/**
 * Loads Zendesk when the widget key env var is set; JWT auth for logged-in users.
 *
 * **Remove the default FAB entirely:** Admin Center → Web Widget → Launcher shape → **Custom launcher**
 * (then only `openZendeskMessenger` / APIs surface the widget).
 */
export function syncZendeskWidgetForAuth(isLoggedIn: boolean) {
  const widgetKey = getZendeskWidgetKey()

  if (!widgetKey) return

  userWantsZendesk = isLoggedIn

  if (!isLoggedIn) {
    runZendeskCommand('messenger', 'logoutUser')
    runZendeskCommand('messenger', 'hide')

    return
  }

  const zendeskEmbed = window.zE

  if (typeof zendeskEmbed === 'function') {
    ensureMessengerCloseRehidesLauncher()
    applyMessengerHeaderAlwaysVisible()

    void runZendeskMessengerJwtLogin()

    runZendeskCommand('messenger', 'hide')

    return
  }

  if (document.getElementById(SCRIPT_ID)) return // only inject snippet if it's not already loaded

  const snippetScript = document.createElement('script')

  snippetScript.id = SCRIPT_ID
  snippetScript.async = true
  snippetScript.src = `https://static.zdassets.com/ekr/snippet.js?key=${widgetKey}`
  snippetScript.onload = () => {
    if (userWantsZendesk) {
      ensureMessengerCloseRehidesLauncher()
      applyMessengerHeaderAlwaysVisible()
      void runZendeskMessengerJwtLogin()
      runZendeskCommand('messenger', 'hide')
    } else {
      runZendeskCommand('messenger', 'logoutUser')
      runZendeskCommand('messenger', 'hide')
    }
  }

  document.body.appendChild(snippetScript)
}

export function useZendeskAuthSync(options: { allowedHost: boolean }) {
  const authStore = useAuthStore()
  const flags = useFeatureFlags()

  watchEffect(() => {
    if (!options.allowedHost) return

    const widgetEnabled = flags.initialised && flags.hasFeature('feature/zendesk_widget')

    if (!widgetEnabled) {
      syncZendeskWidgetForAuth(false)

      return
    }

    syncZendeskWidgetForAuth(authStore.isAuthenticated)
  })
}
